import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  Ride,
  RideList,
  type CreateRideBody,
  ImportRideResult,
  type ImportRideBody,
  type UpdateRideBody,
} from '@klunkers/shared'
import { api } from '@/api/http'
import { useComponentsStore } from './components'
import { useIntervalsStore } from './intervals'

/** Rides drive usage. Any change here makes cached part usage stale. */
export const useRidesStore = defineStore('rides', () => {
  const byBike = ref<Record<string, Ride[]>>({})

  function invalidateUsage() {
    const components = useComponentsStore()
    components.byBike = {}
    components.details = {}
    // Riding changes interval status too.
    void useIntervalsStore().refresh()
  }

  /** Rides changed elsewhere (a Strava sync): drop what is cached. */
  function stale() {
    byBike.value = {}
    invalidateUsage()
  }

  async function loadForBike(bikeId: string, limit = 20) {
    const { rides } = await api(`/rides?bike_id=${bikeId}&limit=${limit}`, { schema: RideList })
    byBike.value = { ...byBike.value, [bikeId]: rides }
    return rides
  }

  async function log(body: CreateRideBody) {
    const ride = await api('/rides', { method: 'POST', body, schema: Ride })
    byBike.value = {}
    invalidateUsage()
    return ride
  }

  /** Import rides; returns how many were new (repeats are skipped by the API). */
  async function importRides(
    bodies: ImportRideBody[],
  ): Promise<{ added: number; repeats: number; created: Ride[] }> {
    const created: Ride[] = []
    for (const body of bodies) {
      const res = await api('/rides/import', { method: 'POST', body, schema: ImportRideResult })
      if (res.created) created.push(res.ride)
    }
    byBike.value = {}
    invalidateUsage()
    return { added: created.length, repeats: bodies.length - created.length, created }
  }

  async function update(id: string, body: UpdateRideBody) {
    const ride = await api(`/rides/${id}`, { method: 'PATCH', body, schema: Ride })
    byBike.value = {}
    invalidateUsage()
    return ride
  }

  async function remove(id: string) {
    await api(`/rides/${id}`, { method: 'DELETE' })
    byBike.value = {}
    invalidateUsage()
  }

  return { byBike, loadForBike, log, importRides, update, remove, stale }
})
